export const PILOT_TIMEZONE = 'Asia/Ho_Chi_Minh';
export function validZone(value) {
  try { new Intl.DateTimeFormat('en', { timeZone: value || PILOT_TIMEZONE }).format(); return value || PILOT_TIMEZONE; }
  catch { return PILOT_TIMEZONE; }
}
function parts(value, timeZone) {
  return Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(value).map((part) => [part.type, part.value]));
}
export function venueDate(value, timeZone) {
  const p = parts(value, timeZone);
  return `${p.year}-${p.month}-${p.day}`;
}
export function venueDateTime(value, timeZone) {
  const p = parts(new Date(value), timeZone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}`;
}
export function fixtureTimeToUtc(value, timeZone) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value)) throw new Error('Choose a valid fixture date and time.');
  const wall = value.length === 16 ? `${value}:00` : value;
  const desired = Date.parse(`${wall}Z`);
  if (!Number.isFinite(desired) || new Date(desired).toISOString().slice(0, 19) !== wall) throw new Error('Choose a valid fixture date and time.');
  // Discover the offsets on both sides of a timezone transition. Never silently
  // choose one occurrence of a repeated clock time or normalize a missing hour.
  const offsets = new Set();
  for (let hours = -36; hours <= 36; hours += 6) {
    const sample = desired + hours * 3600000;
    offsets.add(Date.parse(`${venueDateTime(sample, timeZone)}Z`) - sample);
  }
  const candidates = [...offsets].map((offset) => desired - offset).filter((candidate) => venueDateTime(candidate, timeZone) === wall);
  if (!candidates.length) throw new Error('This local time does not exist because the clocks change. Check the kickoff time.');
  if (candidates.length > 1) throw new Error('This local time occurs twice. Use a verified fixture import with an explicit UTC offset.');
  return new Date(candidates[0]).toISOString();
}
function midnight(date, timeZone) {
  const desired = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(desired) || new Date(desired).toISOString().slice(0, 10) !== date) throw new Error('Choose a valid date');
  let candidate = desired;
  for (let attempt = 0; attempt < 5; attempt++) {
    const p = parts(new Date(candidate), timeZone);
    const wall = Date.parse(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}Z`);
    if (wall === desired) return new Date(candidate).toISOString();
    candidate += desired - wall;
  }
  throw new Error('This local midnight is unavailable; choose another date');
}
export function sportsWindow(date, days, timeZone) {
  const from = midnight(date, timeZone);
  const end = new Date(`${date}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + Number(days));
  return { from, to: midnight(end.toISOString().slice(0, 10), timeZone) };
}
export function displayTime(value, timeZone) {
  if (!value) return 'Not supplied';
  return new Date(value).toLocaleString('en-GB', { timeZone, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}
export function displayWindow(from, to, timeZone) {
  const start = displayTime(from, timeZone);
  if (!to) return start;
  const end = venueDate(new Date(from), timeZone) === venueDate(new Date(to), timeZone)
    ? new Date(to).toLocaleTimeString('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    : displayTime(to, timeZone);
  return `${start} – ${end}`;
}
export function utcOffset(value, timeZone) {
  return new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'longOffset' }).formatToParts(value)
    .find((part) => part.type === 'timeZoneName').value.replace('GMT', 'UTC');
}
export function browserWindow(preset, date, days, timeZone, now = new Date()) {
  // A one-millisecond interval expresses "on air at this instant" using the
  // existing half-open programme-overlap contract, including overnight listings.
  if (preset === 'now') return { from: now.toISOString(), to: new Date(now.getTime() + 1).toISOString() };
  return sportsWindow(date, days, timeZone);
}
